import {observer} from 'mobx-react';
import React, {useCallback, useEffect, useState} from 'react';
import s from '../../s';
import {rec_prod} from '../../api';
import {Swiper, SwiperSlide} from 'swiper/react';
import {Keyboard, Navigation} from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import ProductItem from './product-item';
import {hasRelatedProductImage} from './image-fallback/related-product-image';

export type RelatedProduct = {
    id: number;
    title: string;
    src: string;
    srcFallback: string;
    link: string;
    price: number;
};

const RelatedProducts: React.FC = observer(() => {
    const [relatedProducts, setRelatedProducts] = useState<RelatedProduct[]>([]);
    // products whose every image URL failed to load - they are not shown at all
    const [noImageIds, setNoImageIds] = useState<number[]>([]);
    const ready = s.ready;
    // rec_prod sends the current grad school, which picks the grad-school-type mapped
    // store's product (slot #3) - refetch when a lino selection changes it
    const gradSchoolID = s.currentGradSchoolID;

    useEffect(() => {
        if (!ready) {
            return;
        }

        let cancelled = false;

        rec_prod().then(arr => {
            if (cancelled) {
                return;
            }

            setNoImageIds([]);
            setRelatedProducts(
                arr
                    .filter(p => p.ProductID !== s.prod.ProductID)
                    .map(p => ({
                        id: p.ProductID,
                        title: p.ProductTitle,
                        src: p.image_url,
                        srcFallback: p.image_url_th,
                        link: p.url,
                        price: p.price,
                    }))
                    .filter(p => hasRelatedProductImage(p.src, p.srcFallback)),
            );
        });

        return () => {
            cancelled = true;
        };
    }, [ready, gradSchoolID]);

    const handleImageUnavailable = useCallback((productId: number) => {
        setNoImageIds(prev => (prev.includes(productId) ? prev : [...prev, productId]));
    }, []);

    const visibleProducts = relatedProducts.filter(p => !noImageIds.includes(p.id));

    if (!ready || visibleProducts.length < 2) {
        return null;
    }

    return (
        <div id="related-products">
            <h2 className="related-products-heading">We also recommend</h2>
            <Swiper
                id="related-products-swiper"
                grabCursor={true}
                keyboard={{enabled: true}}
                navigation={true}
                spaceBetween={20}
                slidesPerView="auto"
                updateOnWindowResize={true}
                modules={[Keyboard, Navigation]}
                className="related-products-swiper"
                aria-roledescription="carousel"
                role="region"
                aria-label="Recommended products"
            >
                {visibleProducts.map(p => (
                    <SwiperSlide key={p.id}>
                        <ProductItem
                            product={p}
                            onImageUnavailable={handleImageUnavailable}
                        />
                    </SwiperSlide>
                ))}
            </Swiper>
        </div>
    );
});

export default RelatedProducts;
